import { useTranslation } from "react-i18next";
import type {
  ReportableCampaignStatusName,
  ReportCampaignOptionsResponse,
} from "@multi-chat/shared";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { campaignStatusLabelKey } from "./campaign-report-presentation.js";

const ALL = "__ALL__";

export type CampaignReportFilterValues = {
  status?: ReportableCampaignStatusName;
  endpointId?: number;
  template?: string;
};

export function CampaignReportFilters({
  options,
  values,
  onChange,
  idPrefix = "reports-campaigns",
}: {
  options: ReportCampaignOptionsResponse | null;
  values: CampaignReportFilterValues;
  onChange: (patch: Partial<CampaignReportFilterValues>) => void;
  idPrefix?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const statuses = options?.statuses ?? [];
  const senders = options?.senders ?? [];
  const templates = options?.templates ?? [];

  return (
    <div className="grid w-full min-w-0 gap-1.5" data-testid={`${idPrefix}-filters`}>
      <label
        className="grid w-full min-w-0 gap-0.5 text-[0.75rem]"
        data-testid={`${idPrefix}-status-filter`}
      >
        <span className="text-muted">{t("reports.campaigns.filters.status")}</span>
        <SimpleSelect
          value={values.status ?? ALL}
          onValueChange={(value) =>
            onChange({
              status: value === ALL ? undefined : (value as ReportableCampaignStatusName),
            })
          }
          ariaLabel={t("reports.campaigns.filters.status")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.campaigns.filters.allStatuses") },
            ...statuses.map((status) => ({
              value: status,
              label: t(campaignStatusLabelKey(status)),
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.campaigns.filters.sender")}</span>
        <SimpleSelect
          value={values.endpointId != null ? String(values.endpointId) : ALL}
          onValueChange={(value) =>
            onChange({ endpointId: value === ALL ? undefined : Number(value) })
          }
          ariaLabel={t("reports.campaigns.filters.sender")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.campaigns.filters.allSenders") },
            ...senders.map((sender) => ({
              value: String(sender.endpointId),
              label:
                [sender.displayName, sender.phoneNumber].filter(Boolean).join(" · ") ||
                `#${sender.endpointId}`,
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.campaigns.filters.template")}</span>
        <SimpleSelect
          value={values.template ?? ALL}
          onValueChange={(value) =>
            onChange({ template: value === ALL ? undefined : value })
          }
          ariaLabel={t("reports.campaigns.filters.template")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.campaigns.filters.allTemplates") },
            ...templates.map((template) => ({
              value: template,
              label: template,
            })),
          ]}
        />
      </label>

      {values.endpointId != null ? (
        <p className="m-0 hidden text-[0.7rem] text-muted sm:block">
          <LtrValue>
            {senders.find((s) => s.endpointId === values.endpointId)?.phoneNumber ?? ""}
          </LtrValue>
        </p>
      ) : null}
    </div>
  );
}
